import { apiFetch } from "@storytell/client";
import SparkMD5 from "spark-md5";

interface SaveArtifactResponse {
  assetId: string;
}

export class ArtifactSaveError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public originalError?: unknown
  ) {
    super(message);
    this.name = "ArtifactSaveError";
  }
}

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = Number.parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes;
}

function binaryToBase64(binary: string): string {
  return btoa(binary);
}

function hexToBase64(hexString: string): string {
  const bytes = hexToBytes(hexString);
  const binaryString = Array.from(bytes)
    .map((byte) => String.fromCharCode(byte))
    .join("");
  return binaryToBase64(binaryString);
}

async function calculateMD5(blob: Blob): Promise<string> {
  const arrayBuffer = await blob.arrayBuffer();
  const spark = SparkMD5.ArrayBuffer.hash(arrayBuffer);
  return hexToBase64(spark);
}

interface SaveArtifactOptions {
  pageUrl?: string;
  pageTitle?: string;
  capturedAt?: string;
  mimeType?: string;
  extension?: string;
}

export async function saveArtifactToKB(
  pdfBlob: Blob,
  displayName: string,
  authToken: string,
  options?: SaveArtifactOptions
): Promise<SaveArtifactResponse> {
  const filename = `${displayName}${options?.extension ?? ".pdf"}`;
  const md5Hash = await calculateMD5(pdfBlob);

  try {
    const metadata: Record<string, unknown> = {
      filename,
      fileSizeInBytes: pdfBlob.size,
      mimeType: options?.mimeType ?? "application/pdf",
      md5: md5Hash,
    };

    if (options?.pageUrl) {
      metadata.pageUrl = options.pageUrl;
    }
    if (options?.pageTitle) {
      metadata.pageTitle = options.pageTitle;
    }
    if (options?.capturedAt) {
      metadata.capturedAt = options.capturedAt;
    }

    const createResponse = await apiFetch("/assets", {
      method: "POST",
      authToken,
      scope: "project-scoped",
      body: JSON.stringify(metadata),
    });

    if (!createResponse.ok) {
      throw new ArtifactSaveError("Failed to create asset", createResponse.status);
    }

    const createData: unknown = await createResponse.json();
    const created = typeof createData === "object" && createData !== null ? createData : {};
    const assetId = "id" in created && typeof created.id === "string" ? created.id : undefined;
    const uploadMethod = "method" in created ? created.method : undefined;
    const uploadUrl = "signedUrl" in created && typeof created.signedUrl === "string" ? created.signedUrl : undefined;
    if (!assetId) {
      throw new ArtifactSaveError("Failed to create asset");
    }

    if (uploadMethod === "DIRECT") {
      const uploadFormData = new FormData();
      uploadFormData.append("file", pdfBlob, filename);

      const uploadResponse = await apiFetch(`/assets/${assetId}/upload`, {
        method: "POST",
        authToken,
        scope: "project-scoped",
        body: uploadFormData,
      });

      if (!uploadResponse.ok) {
        throw new ArtifactSaveError("Failed to upload file", uploadResponse.status);
      }
    } else if (uploadMethod === "SIGNED_URL") {
      if (!uploadUrl) {
        throw new ArtifactSaveError("Failed to upload file");
      }
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: pdfBlob,
      });

      if (!uploadResponse.ok) {
        throw new ArtifactSaveError("Failed to upload file", uploadResponse.status);
      }
    }

    const completeResponse = await apiFetch(`/assets/${assetId}/uploaded`, {
      method: "PUT",
      authToken,
      scope: "project-scoped",
    });

    if (!completeResponse.ok) {
      throw new ArtifactSaveError("Failed to complete upload", completeResponse.status);
    }

    const labelResponse = await apiFetch("/labels/system/artifact", {
      method: "GET",
      authToken,
      scope: "project-scoped",
    });

    if (!labelResponse.ok) {
      throw new ArtifactSaveError("Failed to get artifact label", labelResponse.status);
    }

    const labelData: unknown = await labelResponse.json();
    const labelId = typeof labelData === "object" && labelData !== null && "id" in labelData ? labelData.id : undefined;

    const associateResponse = await apiFetch("/label-associations", {
      method: "POST",
      authToken,
      scope: "project-scoped",
      body: JSON.stringify([
        {
          labelId,
          resourceId: assetId,
          resourceType: "asset",
        },
      ]),
    });

    if (!associateResponse.ok) {
      console.warn("Failed to associate label with artifact");
    }

    return { assetId };
  } catch (error) {
    if (error instanceof ArtifactSaveError) {
      throw error;
    }

    if (error instanceof TypeError) {
      throw new ArtifactSaveError("Network error, please check your connection", undefined, error);
    }

    throw new ArtifactSaveError("Unexpected error occurred", undefined, error);
  }
}
